import React from 'react';
import swal from 'sweetalert';
import { toast } from 'react-toastify';
import { Loader2, Trash2 } from 'lucide-react';
import i18n from '../../../../../i18n';
import { checkDiscountEligibility, applyDiscount, removeDiscount } from '../services/auctionApprovalServices';
import { CustomModal } from '../../../../common/CustomModal';

export class AuctionDiscountModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      removing: false,
      checking: false,
      eligibility: null,
      remarks: '',
      removeRemarks: '',
    };
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (this.props.show && !prevProps.show && this.props.auction) {
      this.setState({ remarks: '', removeRemarks: '' });
      this.checkEligibility();
    }
  }

  checkEligibility = async () => {
    const { auction } = this.props;
    if (!auction) return;

    this.setState({ checking: true, eligibility: null });
    try {
      const response = await checkDiscountEligibility({
        user: auction.user_id,
        group: auction.group_id,
      });
      if (response && response.success) {
        this.setState({ eligibility: response.data });
      } else {
        this.setState({ eligibility: { eligible: false, reason: 'error' } });
      }
    } catch (err) {
      console.error(err);
      this.setState({ eligibility: { eligible: false, reason: 'error' } });
    } finally {
      this.setState({ checking: false });
    }
  };

  getReasonMessage = (reason) => {
    const messages = {
      not_staff: 'This user is not eligible for staff discounts.',
      already_used_in_group: 'This employee already used their discount in this group.',
      yearly_limit_reached: 'This employee has already used 2 discounts this year.',
      no_wins_in_group: 'This employee has no winning auctions in this group.',
      error: 'Could not check discount eligibility.',
    };
    return messages[reason] || 'Not eligible for a discount.';
  };

  async handleSubmit(event) {
    event.preventDefault();
    const { auction } = this.props;
    const { eligibility, remarks } = this.state;

    if (!eligibility || !eligibility.eligible) return;

    this.setState({ loading: true });
    try {
      const response = await applyDiscount({
        user: auction.user_id,
        group: auction.group_id,
        auction: eligibility.auction_id,
        remarks,
      });

      if (response && response.success) {
        swal(i18n.t('success'), 'Discount applied successfully', 'success');
        if (this.props.onSuccess) {
          this.props.onSuccess();
        } else {
          this.props.onClose();
        }
      } else {
        swal(i18n.t('failed'), response.message, 'error');
      }
    } catch (err) {
      console.error(err);
      swal(i18n.t('failed'), 'Something went wrong applying the discount', 'error');
    } finally {
      this.setState({ loading: false });
    }
  }

  handleRemoveDiscount = () => {
    const { auction } = this.props;

    swal({
      title: i18n.t('remove_discount'),
      text: 'Are you sure you want to remove this discount? The auction total will revert to the original amount.',
      icon: 'warning',
      buttons: [i18n.t('cancel'), i18n.t('confirm')],
      dangerMode: true,
    }).then(async (confirmed) => {
      if (!confirmed) return;

      this.setState({ removing: true });
      try {
        const response = await removeDiscount(auction.id, this.state.removeRemarks);

        if (response && response.success) {
          toast.success('Discount removed successfully');
          if (this.props.onSuccess) {
            this.props.onSuccess();
          } else {
            this.props.onClose();
          }
        } else {
          swal(i18n.t('failed'), response.message || 'Failed to remove discount', 'error');
        }
      } catch (err) {
        console.error(err);
        swal(i18n.t('failed'), 'Something went wrong removing the discount', 'error');
      } finally {
        this.setState({ removing: false });
      }
    });
  };

  render() {
    const { show, onClose, auction } = this.props;
    const { checking, eligibility, loading, removing, remarks, removeRemarks } = this.state;

    // Check if this auction already has an active discount
    const hasActiveDiscount =
      auction && Number(auction.discount_amount) > 0;

    return (
      <CustomModal
        isOpen={show}
        header={hasActiveDiscount ? 'Staff Discount' : 'Apply Staff Discount'}
        onClose={onClose}
      >
        <div className="wallet-form-container">

          {/* ════════ Active discount — show details + remove option ════════ */}
          {hasActiveDiscount && (
            <div>
              <div className="stats-section-center">
                <div className="stat-box-center">
                  <div className="stat-label-center">Bid Amount</div>
                  <div className="stat-value-center">{auction.bid_amount} OMR</div>
                </div>
                <div className="stat-box">
                  <div className="stat-label-center">
                    Discount ({auction.discount_percentage}%)
                  </div>
                  <div className="stat-value-center amount">
                    -{Number(auction.discount_amount).toFixed(3)} OMR
                  </div>
                </div>
                <div className="stat-box">
                  <div className="stat-label-center">Total After Discount</div>
                  <div className="stat-value-center">
                    {(Number(auction.total_amount)).toFixed(3)} OMR
                  </div>
                </div>
              </div>

              <div
                style={{
                  background: '#fef3c7',
                  border: '1px solid #fbbf24',
                  borderRadius: 8,
                  padding: '12px 14px',
                  margin: '16px 0',
                  fontSize: 13,
                  color: '#92400e',
                }}
              >
                This auction already has an active staff discount applied. You can
                remove it below if needed — the total will revert to the original
                amount.
              </div>

              <div className="wallet-remarks-section" style={{ marginBottom: 16 }}>
                <label className="wallet-form-label">Reason for removal (optional)</label>
                <textarea
                  value={removeRemarks}
                  onChange={(e) => this.setState({ removeRemarks: e.target.value })}
                  rows={2}
                  className="wallet-textarea"
                  placeholder="e.g. Applied to wrong auction"
                />
              </div>

              <div className="wallet-actions">
                <button type="button" className="modal-btn btn-cancel" onClick={onClose}>
                  {i18n.t('close')}
                </button>
                <button
                  type="button"
                  className="modal-btn btn-confirm"
                  style={{ background: '#ef4444' }}
                  onClick={this.handleRemoveDiscount}
                  disabled={removing}
                >
                  {removing ? (
                    <span style={{ color: 'white', display: 'flex', alignItems: 'center', gap: 5 }}>
                      <Loader2 className="animate-spin" size={16} /> Removing...
                    </span>
                  ) : (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      <Trash2 size={16} /> Remove Discount
                    </span>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* ════════ No active discount — check eligibility + apply ════════ */}
          {!hasActiveDiscount && (
            <>
              {checking && (
                <div className="stats-section-center">
                  <Loader2 className="animate-spin" size={20} />
                  <span style={{ marginLeft: 8 }}>Checking eligibility...</span>
                </div>
              )}

              {!checking && eligibility && !eligibility.eligible && (
                <div className="wallet-error" style={{ padding: '12px 0' }}>
                  {this.getReasonMessage(eligibility.reason)}
                </div>
              )}

              {!checking && eligibility && eligibility.eligible && (
                <form className="wallet-form" autoComplete="off">
                  <div className="stats-section-center">
                    <div className="stat-box-center">
                      <div className="stat-label-center">Winning Bid</div>
                      <div className="stat-value-center">{eligibility.bid_amount} OMR</div>
                    </div>
                    <div className="stat-box">
                      <div className="stat-label-center">
                        Discount ({eligibility.discount_percentage}%)
                      </div>
                      <div className="stat-value-center amount">
                        -{eligibility.discount_amount} OMR
                      </div>
                    </div>
                  </div>

                  <div className="wallet-form-content">
                    <p style={{ fontSize: 13, color: '#666' }}>
                      {eligibility.remaining_this_year} of 2 yearly discounts remaining for this
                      employee after this one. Auction #{eligibility.auction_id} (their highest
                      win in this group).
                    </p>

                    <div className="wallet-remarks-section">
                      <label className="wallet-form-label">{i18n.t('remarks')}</label>
                      <textarea
                        value={remarks}
                        onChange={(e) => this.setState({ remarks: e.target.value })}
                        rows={3}
                        className="wallet-textarea"
                        placeholder="Optional note"
                      />
                    </div>
                  </div>

                  <div className="wallet-actions">
                    <button type="button" className="modal-btn btn-cancel" onClick={onClose}>
                      {i18n.t('cancel')}
                    </button>
                    <button
                      type="button"
                      className="modal-btn btn-confirm"
                      onClick={this.handleSubmit}
                      disabled={loading}
                    >
                      {loading ? (
                        <span
                          style={{
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 5,
                          }}
                        >
                          <Loader2 className="animate-spin" size={16} /> Applying...
                        </span>
                      ) : (
                        'Apply Discount'
                      )}
                    </button>
                  </div>
                </form>
              )}
            </>
          )}
        </div>
      </CustomModal>
    );
  }
}